import { MoreVert } from '@mui/icons-material';
import { IconButton, Menu, MenuItem, Stack } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutateArchiveCropMonitoringById } from '/@/hooks/crop-monitoring/use-crop-monitoring';
import { useFieldById } from '/@/hooks/fields';
import { useHasWriteAccess } from '/@/hooks/session';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import { reverse } from '/@/utils/router';
import CropMonitoringDialog from '/@/views/fields/field-monitoring/components/creation-dialog';

type Options = 'archive' | 'edit' | 'show';

const CropMonitoringMoreOptions = ({
    fieldId,
    cropMonitoring,
    disableOptions,
}: {
    fieldId: number;
    cropMonitoring?: CropMonitoring;
    disableOptions?: Options[];
}) => {
    const [rowActionsAnchorEl, setRowActionsAnchorEl] = useState<HTMLElement | null>(null);
    const [newCMModalOpened, setNewCMModalOpened] = useState(false);
    const navigate = useNavigate();
    const fieldQuery = useFieldById({ id: fieldId });
    const archiveCropMonitoringByIdMutation = useMutateArchiveCropMonitoringById(cropMonitoring?.id);

    const hasV6Stage = (cropMonitoring?.cornCropMonitoring?.stages || []).find((stage) => stage.vegetativeStage === 6);
    const canEditResource = useHasWriteAccess();

    const menuItems: {
        label: string;
        onClick: (e: React.MouseEvent<HTMLElement>) => void;
        disabled?: boolean;
        dataTestId?: string;
    }[] = [
        ...(disableOptions?.includes('edit') || !canEditResource
            ? []
            : [
                  {
                      label: !cropMonitoring?.active ? _('Agregar cultivo') : _('Editar campaña'),
                      onClick: (e) => {
                          setNewCMModalOpened(true);
                      },
                      disabled: Boolean(cropMonitoring?.active && hasV6Stage),
                      dataTestId: 'field-monitoring-more-options-add-crop-button',
                  },
              ]),
        ...(disableOptions?.includes('archive') || !canEditResource
            ? []
            : [
                  {
                      label: _('Archivar'),
                      disabled: !cropMonitoring?.active,
                      onClick: (e) => {
                          archiveCropMonitoringByIdMutation.trigger();
                      },
                      dataTestId: 'field-monitoring-more-options-archive-button',
                  },
              ]),
        ...(disableOptions?.includes('show')
            ? []
            : [
                  {
                      label: _('Ver reporte'),
                      onClick: (e) => {
                          navigate(
                              reverse('cropMonitoring.CornCropMonitoringDetail.report', {
                                  params: { cropMonitoringId: cropMonitoring?.id },
                              })
                          );
                      },
                      disabled: !cropMonitoring?.result,
                      dataTestId: 'field-monitoring-more-options-view-report-button',
                  },
              ]),
    ];

    if (menuItems.length === 0) return null;

    return (
        <Stack direction='row' justifyContent='flex-end'>
            <IconButton
                data-testid='field-monitoring-more-options-icon-button'
                size='small'
                onClick={(e) => {
                    e.stopPropagation();
                    setRowActionsAnchorEl(e.currentTarget);
                }}
                color='primary'
            >
                <MoreVert />
            </IconButton>
            <Menu
                open={!!rowActionsAnchorEl}
                onClose={(e) => {
                    e.stopPropagation();
                    setRowActionsAnchorEl(null);
                }}
                anchorEl={rowActionsAnchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                {menuItems.map((item, index) => (
                    <MenuItem
                        key={index}
                        onClick={(e) => {
                            e.stopPropagation();
                            item.onClick(e);
                            setRowActionsAnchorEl(null);
                        }}
                        data-testid={item.dataTestId}
                        disabled={item.disabled}
                    >
                        {item.label}
                    </MenuItem>
                ))}
            </Menu>
            {newCMModalOpened && (
                <CropMonitoringDialog
                    open={newCMModalOpened}
                    setClose={() => setNewCMModalOpened(false)}
                    field={fieldQuery.data}
                    entry_source='field-detail'
                />
            )}
        </Stack>
    );
};

export default CropMonitoringMoreOptions;
